import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Transient, per-card VIEW state — the third context channel, alongside the ambient
 * read scope and the persisted `config`. It is the seam a header control (a filter
 * toggle, an "only me" switch) writes and the body reads back, WITHOUT touching
 * `config` (that channel is debounced, workspace-shared, and absent for non-editors).
 *
 * It is an opaque `Record<string, unknown>` bag — the kit stays domain-free and never
 * knows what a widget stashes here; the widget's header actions and its body agree on
 * the shape. State is in-memory and scoped to one mounted card: it resets when the
 * card unmounts and is never persisted. A card with no provider (gallery preview,
 * maximized overlay) reads a stable empty bag whose setter is a no-op — never a throw.
 */
export type WidgetViewState = Record<string, unknown>;

export type WidgetViewStateChannel = {
  /** The current bag; empty off-card. */
  state: WidgetViewState;
  /** Shallow-merge a patch into the bag (no-op off-card). */
  patch: (next: WidgetViewState) => void;
};

const EMPTY_STATE: WidgetViewState = {};
const FALLBACK: WidgetViewStateChannel = { state: EMPTY_STATE, patch: () => {} };

const WidgetViewStateContext = createContext<WidgetViewStateChannel | null>(null);

/** Wraps ONE card's header + body so both share the same transient bag. The kit
 *  mounts this in {@link WidgetCard}; a host mounting a body directly may wrap its
 *  own to give the body a writable channel. */
export function WidgetViewStateProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<WidgetViewState>(EMPTY_STATE);
  const patch = useCallback(
    (next: WidgetViewState) => setState((prev) => ({ ...prev, ...next })),
    [],
  );
  const value = useMemo<WidgetViewStateChannel>(() => ({ state, patch }), [state, patch]);
  return (
    <WidgetViewStateContext.Provider value={value}>{children}</WidgetViewStateContext.Provider>
  );
}

/** The card's transient view-state channel. Off-card it returns a stable empty
 *  bag with a no-op `patch`, so reads never throw and writes are simply dropped. */
export function useWidgetViewState(): WidgetViewStateChannel {
  return useContext(WidgetViewStateContext) ?? FALLBACK;
}
